/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { useEffect, useState } from "react";
import { useOtpInput } from "react-otp-input-hook";
import { Row, Col } from "react-bootstrap";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import useCountDown from "react-countdown-hook";
import { OTPData } from "./types";
import { SubmitButton } from "../../../components/buttons/submit-button";
import { useGenerateOTPMutation, useLoginWithOTPMutation } from "./queries";
import { duratiionToMinutes } from "../../../utils/DateUtils";
import { CodeExpired } from "./code-expired";
import { isHabitnuApp } from "../../../utils/white-label";
import { useClientId } from "../../../contexts/branding";
import { fetchAppConfig } from "../../../hooks/use-config";
import { Spacer } from "../../components/spacer";
import { CURRENT_URL } from "../../../constants";

interface Props {
    deliveryMode: "email" | "text";
    email: string;
    redirectPathName?: string;
}

export default function VerifyOTP({
    email,
    deliveryMode,
    redirectPathName,
}: Props): JSX.Element {
    const timeout = localStorage.getItem("timeout") || "600000";
    const [timeLeft, actions] = useCountDown(parseInt(timeout, 10), 1000);
    const [fetchData, setFetchData] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const { mutateAsync: createOTP, isLoading: loading } =
        useGenerateOTPMutation();
    const { mutateAsync: loginWithOTP, isLoading: isLoggingIn } =
        useLoginWithOTPMutation();
    const navigate = useNavigate();
    const clientId = useClientId();
    const { t } = useTranslation();

    const schema = yup.object().shape({
        otpData: yup
            .string()
            .min(6, "Must be exactly 6 digits")
            .max(6, "Must be exactly 6 digits"),
    });

    useEffect(() => {
        actions.start();
    }, [actions]);

    const {
        handleSubmit,
        setValue,
        formState: { isSubmitting },
    } = useForm<OTPData>({
        resolver: yupResolver(schema),
    });

    const onSubmit = ({ otpData }: OTPData) => {
        setFetchData(true);
        loginWithOTP({
            email,
            otp: otpData,
            password: "",
            client_id: clientId,
        })
            .then(({ data }) => {
                if (data.success) {
                    localStorage.setItem("token", data.token);
                } else {
                    setErrorMessage(data.message);
                }
            })
            .then(() => fetchAppConfig()) // once logged in set app config
            .then((data) => {
                const currentUrl = localStorage.getItem(CURRENT_URL);
                let redirectUrl;
                if (redirectPathName) {
                    redirectUrl = redirectPathName;
                } else if (currentUrl) {
                    redirectUrl = currentUrl;
                } else if (data?.nav_menu && data.nav_menu[0]?.url) {
                    redirectUrl = data.nav_menu[0].url;
                } else {
                    redirectUrl = "/app/dashboard";
                }
                navigate(redirectUrl, { replace: true });
                setFetchData(false);
                localStorage.removeItem(CURRENT_URL);
            })
            .catch(({ data }) => {
                setErrorMessage(String(data?.message) || "Failed to login");
                setFetchData(false);
            });
    };

    const onGenerateOtp = () => {
        setErrorMessage("");
        createOTP({ email, deliveryMode, client_id: clientId })
            .then(() => {
                actions.reset();
                actions.start();
            })
            .catch(() => setErrorMessage("Failed to generate OTP"));
    };

    const { register: otpRegister } = useOtpInput({
        focusOnLoad: true,
        numberOfInputs: 6,
        onInputValueChange: (val: string) => {
            setValue("otpData", val);
        },
        defaultInlineStyles: {
            height: "45px",
            width: "45px",
            margin: "0 5px",
            textAlign: "center",
            fontSize: "20px",
            border: "1px solid #253F5B",
            borderRadius: "4px",
        },
    });

    const defaultOptions = { required: true };
    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <CodeExpired
                onGenerateOtp={onGenerateOtp}
                loading={loading}
                show={timeLeft < 1}
            />
            <div className="padding-top-20 padding-right-5">
                <div>
                    <h3
                        className="font-weight-700 display-flex-row-reverse"
                        style={{
                            color:
                                timeLeft > 120000
                                    ? isHabitnuApp()
                                        ? "#00078A"
                                        : "#28a745"
                                    : timeLeft > 60000
                                    ? "#DF6914"
                                    : "#bf392a",
                        }}
                    >
                        {duratiionToMinutes(timeLeft)}
                    </h3>
                </div>
            </div>
            <Row>
                <Col
                    xs={12}
                    md={{ offset: 2, span: 8 }}
                    lg={{ offset: 2, span: 8 }}
                >
                    <div className="login-header-text">
                        {t("login.verify_code_header")}
                    </div>
                    <div className="text-md">
                        {t("login.verify_code_sub_header")}
                    </div>
                    <Spacer height={50} />
                    <div className="d-flex justify-center-container">
                        {Array.from({ length: 6 }, (_, index) => (
                            <input
                                key={index}
                                {...otpRegister(
                                    `digit-${index + 1}`,
                                    defaultOptions
                                )}
                                className={
                                    isHabitnuApp()
                                        ? "thick-bottom-border otp-digit"
                                        : "ada-otp-digit"
                                }
                            />
                        ))}
                    </div>

                    <Spacer height={40} />
                    <div className="text-align-center">
                        <SubmitButton
                            isSubmitting={
                                isSubmitting || isLoggingIn || fetchData
                            }
                            label="login.verify_code"
                            className={
                                isHabitnuApp()
                                    ? "w-100 field-height"
                                    : "w-100 field-height ada-btn-success"
                            }
                        />
                        {errorMessage ? (
                            <div className="text-danger margin-top-5">
                                {t(errorMessage)}
                            </div>
                        ) : null}
                    </div>
                </Col>
            </Row>
        </form>
    );
}
